import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Text, FormControl, ButtonGroup, Textarea } from "@primer/react";
import {
  SyncIcon,
  DownloadIcon,
  ListUnorderedIcon,
  MailIcon,
  DeviceMobileIcon,
  AlertIcon,
} from "@primer/octicons-react";
import { useSelector } from "react-redux";
import { useCommonContext } from "../../../contexts/common";

import { NotifyHelper } from "../../../helpers/toast";
import DataTable from "../../../components/ui/data-table";
import MyButton from "../../../components/ui/button/Button";
import { ComboboxTruong } from "../../../components/combobox-truong";
import ComboboxKyThi from "../../../components/combobox-kythi";
import DanhGiaKetQuaModal from "./danh-gia-ket-qua/DanhGiaKetQuaModal";
import { ketQuaThiApi } from "../../../api/ketQuaThiApi";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { kyThiMonThiApi } from "../../../api/kyThiMonThiApi";
import { trangThaiThiSinhApi } from "../../../api/trangThaiThiSinhApi";
import KetQuaThiSummary from "./summary-info/KetQuaThiSumary";
import moment from "moment";
import NhanXetInput from "./NhanXetInput";

interface IThiSinh {
  id: number;
  trang_thai: string;
  so_bao_danh: string;
  ma_hs: string;
  ho_ten: string;
  ngay_sinh: string;
  gioi_tinh: string;
  ex_trangthai_thisinh_id: number;
  nhan_xet_1: string;
  nhan_xet_2: string;
  [key: string]: any;
}

const KetQuaThiPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [thiSinhs, setThiSinhs] = useState<IThiSinh[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [diemTongs, setDiemTongs] = useState<any[]>([]);
  const [monThis, setMonThis] = useState<any[]>([]);
  const [trangThaiThiSinhs, setTrangThaiThiSinhs] = useState<any[]>([]);
  const [showDanhGia, setShowDanhGia] = useState(false);
  const [filterTrangThais, setFilterTrangThais] = useState<number[]>([]);
  const [kyThiKetThuc, setKyThiKetThuc] = useState(false);

  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const { translate } = useCommonContext();

  const [filter, setFilter] = useState({
    dm_truong_id: 0,
    nam_hoc: nam_hoc,
    ex_kythi_id: 0,
  });

  const loadThiSinhs = useCallback(async () => {
    setIsLoading(true);
    setSelectedIds([]);
    try {
      const res = await ketQuaThiApi.SelectThiSinhCoDiemThiAsync({
        ex_kythi_id: filter.ex_kythi_id,
        nam_hoc: nam_hoc,
      });
      if (res.is_success) {
        setThiSinhs(res.data);
      } else {
        setThiSinhs([]);
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    }
    setIsLoading(false);
  }, [filter.ex_kythi_id]);

  const loadTongDiemThis = useCallback(async () => {
    try {
      const res = await ketQuaThiApi.SelectDiemThiByThiSinhAsync({
        ex_kythi_id: filter.ex_kythi_id,
        ex_thisinh_id: 0,
        nam_hoc: nam_hoc,
      });
      if (res.is_success) {
        setDiemTongs(res.data);
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    }
  }, [filter.ex_kythi_id]);

  const loadMonThis = useCallback(async () => {
    try {
      const res = await kyThiMonThiApi.selectByKyThi(filter.ex_kythi_id);
      if (res.is_success) {
        setMonThis(res.data);
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    }
  }, [filter.ex_kythi_id]);

  const loadTrangThaiThiSinhs = useCallback(async () => {
    try {
      const res = await trangThaiThiSinhApi.selectAll();
      if (res.is_success) {
        setTrangThaiThiSinhs(res.data);
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.LoadDataFailed"));
    }
  }, []);

  useEffect(() => {
    loadThiSinhs();
    loadTongDiemThis();
    loadMonThis();
  }, [filter.ex_kythi_id]);

  useEffect(() => {
    loadTrangThaiThiSinhs();
  }, []);

  const handleUpdateKetQuaThi = async (
    thiSinhId: number,
    trangThaiId: number
  ) => {
    try {
      const res = await ketQuaThiApi.UpdateDanhGiaKetQuaThiAsync({
        ex_thisinh_ids: [thiSinhId],
        ex_trangthai_thisinh_id: trangThaiId,
      });
      if (res.is_success) {
        NotifyHelper.Success(translate("Success.Update"));
        loadThiSinhs();
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.UpdateFailed"));
    }
  };

  const handleUpdateNhanXet = async (
    thiSinhId: number,
    nhanXet1: string,
    nhanXet2: string
  ) => {
    try {
      const res = await ketQuaThiApi.UpdateNhanXetKetQuaThiAsync({
        ex_thisinh_id: thiSinhId,
        nhan_xet_1: nhanXet1,
        nhan_xet_2: nhanXet2,
      });
      if (res.is_success) {
        NotifyHelper.Success(translate("Success.Update"));
        loadThiSinhs();
      }
    } catch (error) {
      NotifyHelper.Error(translate("Error.UpdateFailed"));
    }
  };

  const danhSachDiemTongKet = useMemo(() => {
    return thiSinhs
      .filter(
        (x) =>
          filterTrangThais.includes(x.ex_trangthai_thisinh_id) ||
          filterTrangThais.length === 0
      )
      .map((thiSinh) => {
        const result = { ...thiSinh };
        monThis.forEach((monThi) => {
          const diemThi = diemTongs.find(
            (diem) =>
              diem.ex_thisinh_id === thiSinh.id &&
              diem.ex_kythi_monthi_id === monThi.id
          );
          // Add điểm số
          result[`D_${monThi.id}`] = diemThi?.diem_so;
          // Add nhận xét riêng
          result[`NXR_${monThi.id}`] = diemThi?.nhan_xet_rieng || diemThi?.nhan_xet_chung;
        });
        return result;
      });
  }, [thiSinhs, monThis, diemTongs, filterTrangThais]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Toolbar */}
      <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
        <FormControl>
          <ComboboxTruong
            value={filter.dm_truong_id}
            onValueChanged={(value) => {
              setFilter((prev) => ({ ...prev, dm_truong_id: value || 0 }));
            }}
          />
        </FormControl>

        <FormControl>
          <ComboboxKyThi
            value={filter.ex_kythi_id}
            dm_truong_id={filter.dm_truong_id}
            onValueChanged={(value, data) => {
              setFilter((prev) => ({ ...prev, ex_kythi_id: value || 0 }));
              setKyThiKetThuc(data.da_ket_thuc);
            }}
          />
        </FormControl>
        {kyThiKetThuc && (
          <Box
            sx={{
              bg: "danger.emphasis",
              color: "white",
              px: 2,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <AlertIcon size={16} />
            <Text sx={{ fontWeight: "bold" }}>
              {translate("KyThi.DaKetThuc")}
            </Text>
          </Box>
        )}
      </Box>
      <KetQuaThiSummary
        dsThiSinhs={thiSinhs}
        listDanhGias={trangThaiThiSinhs}
        filter={filterTrangThais}
        onFilterChanged={setFilterTrangThais}
      />
      {/* Summary and Actions */}

      {/* Warning for completed exam */}

      {/* Data Table */}
      <DataTable
        height={`${window.innerHeight - 280}px`}
        data={danhSachDiemTongKet}
        isLoading={isLoading}
        searchEnable
        paging={{ enable: true, pageSizeItems: [10, 20, 50, 200, 500] }}
        selection={
          !kyThiKetThuc
            ? {
                mode: "multiple",
                selectedRowKeys: selectedIds,
                onSelectionChanged: setSelectedIds,
              }
            : undefined
        }
        actionComponent={
          <Box sx={{ display: "flex" }}>
            <MyButton
              sx={{ ml: 2 }}
              text={`Đánh giá kết quả thi (${selectedIds.length})`}
              leadingVisual={ListUnorderedIcon}
              variant="primary"
              disabled={selectedIds.length === 0 || kyThiKetThuc}
              onClick={() => setShowDanhGia(true)}
            />
            <MyButton
              sx={{ ml: 2 }}
              text="Gửi SMS"
              leadingVisual={DeviceMobileIcon}
              disabled={true}
            />
            <MyButton
              sx={{ mx: 2 }}
              text="Gửi Email"
              leadingVisual={MailIcon}
              disabled={true}
            />
            <MyButton
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              onClick={() => {
                loadThiSinhs();
                loadMonThis();
                loadTongDiemThis();
              }}
            />
          </Box>
        }
        columns={[
          {
            caption: translate("TT"),
            width: "50px",
            align: "center",
            // cellRender: (_, index) => index + 1
          },
          {
            dataField: "trang_thai",
            caption: translate("Trạng thái"),
            width: "100px",
          },
          {
            dataField: "so_bao_danh",
            caption: translate("Số báo danh"),
            width: "100px",
          },
          {
            dataField: "ma_hs",
            caption: translate("Mã học sinh"),
            width: "100px",
          },
          {
            dataField: "ho_ten",
            caption: translate("Họ tên"),
          },
          {
            dataField: 'ngay_sinh',
            caption: translate('Ngày sinh'), 
            width: '100px',
            cellRender: (data: any) => {
              if (!data.ngay_sinh) return <Text>-</Text>;
              
              const date = moment(data.ngay_sinh);
              if (!date.isValid()) return <Text>-</Text>;
              
              return <Text>{date.format('DD/MM/YYYY')}</Text>;
            }
          },
          {
            dataField: "gioi_tinh",
            caption: translate("Giới tính"),
            width: "80px",
          },
          {
            dataField: "ex_trangthai_thisinh_id",
            caption: translate("Kết quả đánh giá"),
            width: "120px",
            cellRender: (data: any) => {
              const trangThai = trangThaiThiSinhs.find(x => x.id === data.ex_trangthai_thisinh_id);
              return (
                <Text 
                  sx={{ 
                    //color: trangThai?.id === 1 ? 'success.fg' : 'danger.fg',
                    fontWeight: 'bold'
                  }}
                >
                  {trangThai?.ten_trangthai || '-'}
                </Text>
              );
            }
          },
          {
            caption: translate("Kết quả đánh giá"),
            columns: [
              {
                dataField: "nhan_xet_1",
                caption: translate("Nhận xét 1"),
                width: "200px",
                cellRender: (data: any) => (
                  <NhanXetInput
                    value={data.nhan_xet_1}
                    disabled={kyThiKetThuc}
                    onSave={(newValue) => {
                      handleUpdateNhanXet(
                        data.id,
                        newValue,
                        data.nhan_xet_2
                      );
                    }}
                  />
                )
              },
              {
                dataField: "nhan_xet_2",
                caption: translate("Nhận xét 2"),
                width: "200px",
                cellRender: (data: any) => (
                  <NhanXetInput
                    value={data.nhan_xet_2}
                    disabled={kyThiKetThuc}
                    onSave={(newValue) => {
                      handleUpdateNhanXet(
                        data.id,
                        data.nhan_xet_1,
                        newValue
                      );
                    }}
                  />
                )
              }
            ]
          },
          ...monThis.map((monThi) => ({
            caption: translate(monThi.ten_mon_thi),
            columns: [
              {
                dataField: `D_${monThi.id}`,
                caption: translate("Điểm"),
                width: "80px",
                align: "center",
              },
              monThi.nhan_xet_rieng && {
                dataField: `NXR_${monThi.id}`,
                caption: translate("Nhận xét"),
                width: "120px",
              },
            ].filter(Boolean),
          })),
        ]}
      />

      {/* DanhGia Modal */}
      {showDanhGia && (
        <DanhGiaKetQuaModal
          onCancel={() => setShowDanhGia(false)}
          onSuccess={() => {
            setShowDanhGia(false);
            loadThiSinhs();
          }}
          ex_thisinh_ids={selectedIds}
          ex_trangthai_hocsinh={trangThaiThiSinhs}
          is_show_model={showDanhGia}
        />
      )}
    </Box>
  );
};

export default KetQuaThiPage;
